import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Building2,
  CalendarClock,
  Clock3,
  MapPin,
  RefreshCw,
  Search,
  SearchX,
  UserRound,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import { formatTimezoneDate } from '../utils/timezone';
import { STAGE_BADGE_STYLES, STAGES_CONFIG } from '../utils/stages';
import { haptic } from '../utils/vibration';
import useToast from '../hooks/useToast';

const normalizeSearch = (value) => String(value || '')
  .trim()
  .toLocaleLowerCase('ru-RU')
  .replace(/ё/g, 'е')
  .replace(/^№\s*/, '');

const scheduleForOrder = (order) => {
  const hasActiveRevision = Boolean(order?.has_active_revision);
  if (order?.status === 'delivery') {
    return {
      date: hasActiveRevision ? order.revision_return_date : order.delivery_date,
      time: hasActiveRevision ? order.revision_return_time_slot : order.delivery_time_slot,
    };
  }
  return {
    date: hasActiveRevision ? order?.revision_pickup_date : order?.pickup_date,
    time: hasActiveRevision ? order?.revision_pickup_time_slot : order?.pickup_time_slot,
  };
};

const addressForOrder = (order) => (
  order?.status === 'delivery'
    ? (order.revision_return_address_text || order.delivery_address_text || order.pickup_address_text)
    : (order?.revision_pickup_address_text || order?.pickup_address_text || order?.delivery_address_text)
);

export default function DeferredOrdersQueue({ onCountChange }) {
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast(3200);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');

  const loadOrders = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        status: 'deferred',
        active_only: 'true',
        limit: '1000',
      });
      const response = await fetch(`${API_URL}/api/orders?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.detail || `Не удалось загрузить отложенные (${response.status})`);
      }
      const payload = await response.json();
      const rows = Array.isArray(payload) ? payload : [];
      rows.sort((left, right) => {
        const leftDate = new Date(left?.deferred_event?.deferred_at || 0).getTime();
        const rightDate = new Date(right?.deferred_event?.deferred_at || 0).getTime();
        return leftDate - rightDate;
      });
      setOrders(rows);
      onCountChange?.(rows.length);
    } catch (error) {
      showToast(error.message || 'Не удалось загрузить отложенные задания', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [onCountChange, showToast]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const normalizedSearch = normalizeSearch(search);
  const visibleOrders = useMemo(() => {
    if (!normalizedSearch) return orders;
    return orders.filter((order) => [
      order.order_number,
      order.client_name,
      order.pickup_address_text,
      order.delivery_address_text,
      order.revision_pickup_address_text,
      order.revision_return_address_text,
      order.deferred_event?.comment,
      order.deferred_event?.reason_label,
    ].some((value) => normalizeSearch(value).includes(normalizedSearch)));
  }, [normalizedSearch, orders]);

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Поиск по номеру, клиенту или причине"
            className="h-11 w-full rounded-xl border border-gray-200 bg-white pl-10 pr-10 text-sm text-gray-900 shadow-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:focus:border-amber-600 dark:focus:ring-amber-900/30"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label="Очистить поиск"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => loadOrders({ silent: true })}
          disabled={refreshing}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 shadow-sm transition hover:border-amber-300 hover:text-amber-600 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:border-amber-800 dark:hover:text-amber-300"
          title="Обновить"
          aria-label="Обновить отложенные задания"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((item) => (
            <div key={item} className="h-36 animate-pulse rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900" />
          ))}
        </div>
      ) : visibleOrders.length === 0 ? (
        <div className="rounded-2xl bg-white p-8 text-center shadow-sm dark:bg-gray-900">
          <SearchX className="mx-auto mb-3 h-10 w-10 text-gray-400" />
          <div className="font-medium text-gray-900 dark:text-gray-100">
            {normalizedSearch ? 'Ничего не найдено' : 'Отложенных заданий нет'}
          </div>
          <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {normalizedSearch ? 'Попробуйте изменить запрос' : 'Все задания переданы в работу или отменены'}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {visibleOrders.map((order) => {
            const event = order.deferred_event || {};
            const schedule = scheduleForOrder(order);
            const address = addressForOrder(order);
            const scheduledDateText = formatTimezoneDate(schedule.date, false) || 'Дата не указана';
            const clientName = String(order.client_name || '').trim() || 'Клиент не указан';
            const deferredStageName = event.stage_name || order.status;
            const deferredStageConfig = STAGES_CONFIG[deferredStageName] || STAGES_CONFIG.deferred;
            const DeferredStageIcon = deferredStageConfig.icon;
            const deferredStageBadgeClass = STAGE_BADGE_STYLES[deferredStageConfig.color] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
            return (
              <button
                key={order.id}
                type="button"
                onClick={() => {
                  haptic('tap');
                  navigate(`/order/${order.id}?from=my-orders&stage=deferred`);
                }}
                className="w-full overflow-hidden rounded-xl border border-gray-200 bg-white text-left shadow-sm transition hover:border-gray-300 active:scale-[0.99] dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600"
              >
                <div className="px-4 pb-3 pt-2">
                  <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="inline-flex h-6 items-center rounded-md bg-amber-100 px-1.5 text-[11px] font-medium leading-none text-amber-800 ring-1 ring-amber-200 dark:bg-amber-900/35 dark:text-amber-200 dark:ring-amber-900/60">
                        № {order.order_number}
                      </span>
                      <span
                        className={`inline-flex h-6 w-6 items-center justify-center rounded-md text-[11px] font-medium ring-1 ring-black/5 dark:ring-white/10 ${deferredStageBadgeClass}`}
                        title={event.stage_label || deferredStageConfig.label}
                        aria-label={event.stage_label || deferredStageConfig.label}
                      >
                        <DeferredStageIcon className="h-3 w-3" />
                      </span>
                      {order.has_active_revision ? (
                        <span className="inline-flex h-6 items-center rounded-md bg-violet-100 px-1.5 text-[11px] font-medium text-violet-700 dark:bg-violet-900/35 dark:text-violet-200">
                          Повтор
                        </span>
                      ) : null}
                    </div>
                    <div className="flex h-6 items-center justify-end gap-1.5 text-right text-[12px] font-medium leading-4 text-gray-900 dark:text-gray-100">
                      <CalendarClock className="h-3 w-3 shrink-0 text-gray-400 dark:text-gray-500" />
                      <span>{scheduledDateText}</span>
                    </div>
                    <div className="flex min-h-4 min-w-0 items-center gap-1.5 text-[11px] leading-4 text-gray-600 dark:text-gray-300">
                      <Building2 className="h-3 w-3 shrink-0 text-gray-400 dark:text-gray-500" />
                      <span className="truncate">{order.department_name || 'Подразделение не выбрано'}</span>
                    </div>
                    <div className="flex min-h-4 items-center justify-end gap-1.5 text-right text-[11px] font-medium leading-4 text-gray-500 dark:text-gray-400">
                      <Clock3 className="h-3 w-3 shrink-0 text-gray-400 dark:text-gray-500" />
                      <span>{schedule.time || 'Время не указано'}</span>
                    </div>
                  </div>

                  <div className="mt-2 border-t border-gray-200 dark:border-gray-700" />

                  <div className="mt-3 flex min-w-0 items-center gap-1.5 text-[11px] leading-4 text-gray-600 dark:text-gray-300">
                    <UserRound className="h-3 w-3 shrink-0 text-gray-400 dark:text-gray-500" />
                    <span className="truncate">{clientName}</span>
                  </div>

                  <div className="mt-1 flex items-start gap-1 text-[12px] font-medium leading-4 text-gray-950 dark:text-gray-50">
                    <MapPin className="h-3 w-3 shrink-0 text-gray-400 dark:text-gray-500" />
                    <span className="break-words">{address || 'Адрес не указан'}</span>
                  </div>

                  <div className="mt-2 rounded-lg bg-amber-50 px-2.5 py-2 dark:bg-amber-950/25">
                    <div className="flex items-start gap-1.5 text-xs font-semibold leading-4 text-amber-900 dark:text-amber-100">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
                      <span>{event.reason_label || 'Причина не указана'}</span>
                    </div>
                    <div className="mt-1 line-clamp-2 text-xs leading-4 text-amber-800 dark:text-amber-200">
                      {event.comment}
                    </div>
                  </div>

                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] leading-4 text-gray-500 dark:text-gray-400">
                    <span className="inline-flex items-center gap-1">
                      <Clock3 className="h-3 w-3" />
                      Отложено {formatTimezoneDate(event.deferred_at, true) || '—'}
                    </span>
                    <span className="inline-flex min-w-0 items-center gap-1">
                      <UserRound className="h-3 w-3 shrink-0" />
                      <span className="truncate">Было у: {event.previous_assignee_name || 'не указано'}</span>
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

          </div>
  );
}
